/** @jsxImportSource @emotion/react */
import { Tooltip } from '@mui/material';
import { differenceInMilliseconds, format, isPast, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import * as humanizeDuration from 'humanize-duration';
import upperFirst from 'lodash/upperFirst';
import React from 'react';

import { BoulderIcon, Copy } from 'src/common/ui/components';
import { PatientsQuery } from 'src/generated/gql/graphql';
import { SCHEDULE_CHANGE_REASONS, getAllSubTypes, isAllDay } from 'src/shared/util/events';
import type { EventInstance } from 'src/stores/models/event';
import { parseUnknownDate } from 'src/util/parseUnknownDate';

type PartialEventInstance = Pick<
  EventInstance,
  'start' | 'subType' | 'status' | 'type' | 'scheduleChangeReason' | 'scheduleChangeNotes'
>;

export const VisitHistoryDetails = ({
  item,
  type,
}: {
  item: NonNullable<PatientsQuery['items'][number]>;
  type: 'latestCompletedVisit' | 'lastMissedVisit' | 'nextVisit';
}) => {
  if (!item) return <>-</>;

  const visit = ['prescriberActivity', 'coordinatorActivity', 'peerActivity']
    .map(activityType => (item[activityType]?.[type] || null) as PartialEventInstance | null)
    .filter((activity): activity is NonNullable<typeof activity> => Boolean(activity))
    .sort((activityA, activityB) => {
      const dateA = activityA.start;
      const dateB = activityB.start;
      if (!isValid(dateA) || !isValid(dateB)) return 0;
      return parseUnknownDate(dateA) < parseUnknownDate(dateB) ? -1 : 1;
    })[0];

  if (!visit) return <>-</>;

  const isLastMissedActivity = type === 'lastMissedVisit';

  const firstLine = isLastMissedActivity
    ? upperFirst(visit.status ?? undefined)
    : getTimeSinceOrUntil(visit.start);

  let secondLine: string | null = null;
  if (visit.start && isValid(visit.start)) {
    secondLine = isAllDay(visit.type)
      ? formatInTimeZone(visit.start, 'UTC', 'MMM d')
      : format(visit.start, 'MMM d');
  }

  const { scheduleChangeReason, scheduleChangeNotes } = visit;

  let tooltipText: string | undefined;
  if (scheduleChangeReason) {
    tooltipText = SCHEDULE_CHANGE_REASONS[scheduleChangeReason].label;

    if (scheduleChangeNotes) {
      tooltipText += `: ${scheduleChangeNotes}`;
    }
  }

  return (
    <>
      {isLastMissedActivity && (
        <Tooltip title={tooltipText}>
          <BoulderIcon icon="infoOutlined" />
        </Tooltip>
      )}
      <div>
        <Copy variant="body2">
          {visit.subType && (
            <>
              {getAllSubTypes()[visit.subType].label}
              <br />
            </>
          )}
          {firstLine}
          {secondLine && (
            <>
              <br />
              {secondLine}
            </>
          )}
        </Copy>
      </div>
    </>
  );
};

const MILISECONDS_IN_A_MONTH = 1000 * 60 * 60 * 24 * 30;

const getTimeSinceOrUntil = (start: Date | string | null) => {
  const startDate = parseUnknownDate(start);
  const isInThePast = isPast(startDate);
  const timeSinceOrUntil = isInThePast
    ? differenceInMilliseconds(new Date(), startDate)
    : differenceInMilliseconds(startDate, new Date());

  // If time until next visit is in days, hours, or minutes, only show single units by
  // checking if it's been less than a month. Otherwise, display two units.
  const largest = timeSinceOrUntil < MILISECONDS_IN_A_MONTH ? 1 : 2;
  const humanizedTime = `${humanizeDuration(timeSinceOrUntil, {
    largest,
    units: ['y', 'mo', 'd', 'h', 'm'],
    round: true,
  })}`;

  const timeSinceString = isInThePast ? `${humanizedTime} ago` : `in ${humanizedTime}`;
  const timeArray = timeSinceString.split(', ');

  return (
    (timeArray.length === 2 && (
      <>
        {timeArray[0]},
        <br />
        {timeArray[1]}
      </>
    )) || <>{timeArray[0]}</>
  );
};
